/**
 * Centralized OpenAI Text Completion Service
 * 
 * SINGLE SOURCE OF TRUTH for all OpenAI text completions in the platform.
 * 
 * Used by:
 * - Turn Art Story Bible analysis
 * - Storybook generation
 * - Scene analysis
 * - Any GPT-4o/GPT-4o-mini text completion
 * 
 * Architecture:
 * - All requests route through `ai-text-completion` edge function
 * - Server-side API key management (no client exposure)
 * - Automatic retry with exponential backoff
 * - Model fallback (gpt-4o-mini → gpt-3.5-turbo)
 */

import { logger } from '@/lib/logging';
import { supabase } from '@/lib/supabase/client';

// ============================================================================
// Configuration
// ============================================================================

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// ============================================================================
// Types
// ============================================================================

export interface TextCompletionOptions {
  /** Temperature for generation (0.0-2.0, default 0.7) */
  temperature?: number;
  /** Maximum tokens in response */
  maxTokens?: number;
  /** Request JSON response format */
  jsonResponse?: boolean;
  /** System prompt */
  systemPrompt?: string;
  /** Optional image for vision tasks (base64 or data URL) */
  image?: string;
  /** Image detail level for vision (low/high/auto) */
  imageDetail?: 'low' | 'high' | 'auto';
}

export interface TextCompletionResult {
  /** Generated text content */
  content: string;
  /** Parsed JSON if jsonResponse was true */
  json?: unknown;
  /** Model used for generation */
  model: string;
  /** Time taken in ms */
  completionTimeMs: number;
  /** Token usage */
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

// ============================================================================
// Core Function
// ============================================================================

/**
 * Generate text completion using OpenAI via edge function.
 * 
 * This is the SINGLE entry point for text completion across the platform.
 * Routes through `ai-text-completion` edge function for server-side API key management.
 * 
 * @param prompt - The user prompt
 * @param options - Optional generation parameters
 * @returns Generated text and metadata
 */
export async function completeText(
  prompt: string,
  options: TextCompletionOptions = {}
): Promise<TextCompletionResult> {
  const startTime = Date.now();
  const { 
    temperature = 0.7, 
    maxTokens = 1000,
    jsonResponse = false,
    systemPrompt,
    image,
    imageDetail = 'auto',
  } = options;
  
  logger.debug(`[TextCompletion] Calling edge function, vision: ${!!image}, json: ${jsonResponse}`);

  try {
    // Get auth token for edge function
    const { data: { session } } = await supabase.auth.getSession();
    
    const response = await fetch(`${SUPABASE_URL}/functions/v1/ai-text-completion`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || SUPABASE_ANON_KEY}`,
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        prompt,
        systemPrompt,
        temperature,
        maxTokens,
        jsonResponse,
        image,
        imageDetail,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || `HTTP ${response.status}`;
      logger.error(`[TextCompletion] Edge function failed: ${errorMessage}`);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    const completionTimeMs = Date.now() - startTime;

    logger.debug(`[TextCompletion] Success with ${result.model} in ${completionTimeMs}ms`);

    return {
      content: result.content,
      json: result.json,
      model: result.model,
      completionTimeMs,
      usage: result.usage,
    };

  } catch (error) {
    logger.error('[TextCompletion] Request failed:', error);
    throw error;
  }
}

/**
 * Generate text completion with vision (image analysis).
 * 
 * Convenience wrapper for completeText with image parameter.
 * Uses GPT-4o for vision tasks.
 * 
 * @param prompt - The analysis prompt
 * @param imageBase64 - Base64 image data (with or without data URL prefix)
 * @param options - Optional generation parameters
 */
export async function completeTextWithVision(
  prompt: string,
  imageBase64: string,
  options: Omit<TextCompletionOptions, 'image'> = {}
): Promise<TextCompletionResult> {
  return completeText(prompt, {
    ...options,
    image: imageBase64,
  });
}

// ============================================================================
// Exports
// ============================================================================

export default {
  completeText,
  completeTextWithVision,
};
